import { t } from 'i18next';
import { parseISO, startOfDay, startOfMonth, startOfWeek, subDays } from 'date-fns';
import type {
  LookerDataset,
  LookerReportBuildState,
  LookerReportGranularity,
  LookerReportHealthState,
  LookerReportParameters,
  PeriodicReport,
} from '@brand24-original/llm-types';
import type { ColorTagColor } from '../../components/color-tag/ColorTag';
import type { Addons } from '../../types/account/Addons';
import { formatLongDate } from '../date/formatLongDate';

export const getStatusName = (status: string, provider?: string): string => {
  switch (status) {
    case 'ACTIVE':
      return t('active');
    case 'PAUSED':
      return t('paused');
    case 'ERROR':
      return t('error');
    case 'DISABLED':
      return t('disabled');
    case 'PENDING':
      // For Looker Studio reports, show "Pending URL", for others just "PENDING"
      if (provider === 'LOOKER_STUDIO') {
        return t('pendingUrl');
      }
      return t('pending');
    default:
      return t('unknown');
  }
};

export const getStatusColor = (status: string): ColorTagColor => {
  switch (status) {
    case 'ACTIVE':
      return 'green';
    case 'PAUSED':
      return 'orange';
    case 'ERROR':
      return 'red';
    case 'PENDING':
      return 'orange';
    default:
      return 'grey';
  }
};

export const getTypeName = (type: string): string => {
  switch (type) {
    case 'DAILY':
      return t('daily');
    case 'WEEKLY':
      return t('weekly');
    case 'MONTHLY':
      return t('monthly');
    case 'CUSTOM':
      return t('custom');
    default:
      return t('unknown');
  }
};

// Google serwuje raporty pod obiema domenami (linki z lookerstudio bywają przekierowane na datastudio).
const LOOKER_STUDIO_HOSTS = ['lookerstudio.google.com', 'datastudio.google.com'];

/**
 * Adres ZAPISANEGO raportu (`/reporting/<id>/…`). Adres kreatora (`/reporting/create?…`) odrzucamy:
 * jest na tej samej domenie, ale za każdym otwarciem zakłada nowy raport zamiast pokazać gotowy.
 */
export const isSavedLookerStudioReportUrl = (value: string): boolean => {
  try {
    const url = new URL(value.trim());
    const match = url.pathname.match(/\/reporting\/([^/]+)/);
    return LOOKER_STUDIO_HOSTS.includes(url.hostname) && !!match && match[1] !== 'create';
  } catch {
    return false;
  }
};

// ---- Looker Studio multi-dataset export -------------------------------------------------------------
// Runtime mirrors of the llm-common definitions (lookerDatasets.ts, lookerReportFormat.ts): the frontend
// cannot import llm-common, so the values live here, pinned to the shared llm-types unions.

/** Display order of the datasets — the order of the tabs in the workbook. */
export const LOOKER_DATASETS = ['brands', 'prompts', 'domains', 'urls', 'context', 'topics'] as const satisfies readonly LookerDataset[];

/** The owner addon a dataset needs; null = always available. */
export const LOOKER_DATASET_ADDON: Readonly<Record<LookerDataset, Addons | null>> = {
  brands: null,
  prompts: null,
  domains: 'SOURCES_STATS',
  urls: 'SOURCES_STATS',
  context: 'CONTEXT_INSIGHTS',
  topics: 'CONTEXT_INSIGHTS',
};

/** The datasets an owner with these addons can export — the rule the backend validates the body with. */
export const availableLookerDatasets = (addons: readonly string[] | null | undefined): LookerDataset[] =>
  LOOKER_DATASETS.filter((dataset) => {
    const addon = LOOKER_DATASET_ADDON[dataset];
    return addon === null || (addons ?? []).includes(addon);
  });

export const LOOKER_GRANULARITIES = ['DAILY', 'WEEKLY', 'MONTHLY'] as const satisfies readonly LookerReportGranularity[];

/** Days a new report starts with — `INITIAL_WINDOW_DAYS` in llm-common `reportPeriods.ts`. */
export const LOOKER_INITIAL_DAYS = 30;

/** Periods the sheet keeps — `RETENTION_PERIODS` in llm-common `reportPeriods.ts`. */
export const LOOKER_RETENTION: Readonly<Record<LookerReportGranularity, number>> = {
  DAILY: 90,
  WEEKLY: 52,
  MONTHLY: 24,
};

const lookerPeriodStart = (granularity: LookerReportGranularity, day: Date): Date => {
  if (granularity === 'WEEKLY') return startOfWeek(day, { weekStartsOn: 1 });
  if (granularity === 'MONTHLY') return startOfMonth(day);
  return startOfDay(day);
};

/**
 * The first day a new report's sheet covers, as the backend computes it: the last 30 days, widened to
 * whole periods. A project with a shorter history starts at its first day with data instead.
 */
export const lookerInitialStart = (granularity: LookerReportGranularity, today: Date = new Date()): Date =>
  lookerPeriodStart(granularity, subDays(today, LOOKER_INITIAL_DAYS - 1));

/** The day an existing report's data starts: its oldest period, or — while the first build runs — its window. */
export const lookerDataStart = (granularity: LookerReportGranularity, parameters: LookerReportParameters): Date | null => {
  const day = parameters.oldestPeriodStart ?? parameters.build.windowStart;
  return day ? lookerPeriodStart(granularity, parseISO(day)) : null;
};

/** A day in the UI language: „7 września 2026” / “September 7, 2026”. */
export const formatLookerDay = (day: Date, language: string | null | undefined): string =>
  formatLongDate(day, language);

/**
 * The parameters of a report in the current format; null for a legacy one (created before the
 * multi-dataset export) — told apart by `build` alone, as in llm-common.
 */
export const currentLookerParameters = (report: Pick<PeriodicReport, 'config'>): LookerReportParameters | null => {
  const parameters = report.config?.parameters;
  if (!parameters || !('build' in parameters) || !parameters.build) {
    return null;
  }
  return parameters;
};

export const isLegacyLookerReport = (report: Pick<PeriodicReport, 'provider' | 'config'>): boolean =>
  report.provider === 'LOOKER_STUDIO' && currentLookerParameters(report) === null;

const DATASET_LABEL_KEYS: Readonly<Record<LookerDataset, string>> = {
  brands: 'lookerDatasetBrands',
  prompts: 'lookerDatasetPrompts',
  domains: 'lookerDatasetDomains',
  urls: 'lookerDatasetUrls',
  context: 'lookerDatasetContext',
  topics: 'lookerDatasetTopics',
};

export const getDatasetName = (dataset: LookerDataset): string => t(DATASET_LABEL_KEYS[dataset]);

export const getBuildStateName = (state: LookerReportBuildState): string =>
  t(state === 'BUILDING' ? 'lookerBuildBuilding' : state === 'READY' ? 'lookerBuildReady' : 'lookerBuildFailed');

export const getBuildStateColor = (state: LookerReportBuildState): ColorTagColor =>
  state === 'BUILDING' ? 'blue' : state === 'READY' ? 'green' : 'red';

export const getHealthName = (state: LookerReportHealthState): string =>
  t(state === 'OK' ? 'lookerHealthOk' : state === 'DEGRADED' ? 'lookerHealthDegraded' : 'lookerHealthNeedsAttention');

export const getHealthColor = (state: LookerReportHealthState): ColorTagColor =>
  state === 'OK' ? 'green' : state === 'DEGRADED' ? 'orange' : 'red';

/** A manual full recompute is allowed once per 24 hours — the backend answers 429 otherwise. */
export const RECOMPUTE_INTERVAL_MS = 24 * 3_600_000;

/** Why "Recompute all data" is unavailable, or null when it can run. Mirrors the backend's 409/429. */
export const getRecomputeBlockReason = (
  parameters: LookerReportParameters,
  now: Date = new Date()
): 'building' | 'recent' | null => {
  if (parameters.build.state === 'BUILDING') return 'building';
  if (parameters.lastFullRecomputeAt && now.getTime() - Date.parse(parameters.lastFullRecomputeAt) < RECOMPUTE_INTERVAL_MS) {
    return 'recent';
  }
  return null;
};
